<template>
  <div>
    <!-- Wraps on available space rather than a fixed breakpoint: the tags stay on
         the title's row while they fit, and drop below it once they do not. -->
    <div class="flex flex-wrap items-center gap-2 px-5 md:px-10">
      <h1 class="font-bold text-2xl tracking-tight text-primary-dark cursor-pointer transition-colors hover:text-primary-dark-hover no-underline hover:underline" @click="navigate">
        {{ displayName }}
      </h1>
      <div v-if="ratingCount" class="flex items-center gap-2">
        <div class="flex whitespace-nowrap" :aria-label="$t('text-catalogue-rating-average', { score: averageScore })">
          <component
            :is="value <= Math.round(averageScore) ? StarSolid : StarIcon"
            v-for="value in 5"
            :key="value"
            class="w-5 h-5 text-orange"
          />
        </div>
        <span class="text-sm text-body">{{ averageScore }} ({{ ratingCount }})</span>
      </div>
      <div class="flex flex-wrap items-center justify-end gap-2 ms-auto">
        <CatalogueTagParameters :data="variant" />
      </div>
    </div>
    <div class="w-full flex flex-col md:flex-row md:items-start">
      <ContentDetailGallery :variant="variant" :image-size="imageSize" class="md:self-center">
        <template #overlay>
          <CatalogueFavorite :variant="variant" :accent="true" class="absolute top-2.5 end-2.5 z-10" />
        </template>
      </ContentDetailGallery>

      <div :class="[ 'w-full md:w-1/2 pt-5 px-5 md:pl-5 md:pr-10', (perex || content) ? 'mb-9' : '' ]">
        <dl v-if="variant?.parameters?.length">
          <div
            v-for="item in variant.parameters"
            :key="item.id"
            class="flex flex-row items-baseline justify-between py-1.5 border-b border-primary-neutral text-sm"
          >
            <dt class="font-bold text-bolder">{{ item.parameter?.name }}</dt>
            <dd class="text-body text-end">
              {{ item.note || item.value?.value }}
            </dd>
          </div>
        </dl>
        <ClientOnly>
          <div class="flex justify-end">
            <StockAddButton :variant="variant" :accent="true" :label="true" class="mt-5" />
          </div>
        </ClientOnly>
      </div>
    </div>

    <p v-if="perex" class="text-sml text-body font-normal mb-9 px-5 md:px-10">
      {{ perex }}
    </p>

    <article v-if="content" class="prose max-w-none mb-9 px-5 md:px-10" v-html="content" />

    <div class="w-full flex flex-row items-center justify-between space-x-2 mt-2 mb-9 px-5 md:px-10">
      <div class="flex flex-row items-center justify-start space-x-2.5">
        <CatalogueViews :variant="variant" />
        <CatalogueLikes :variant="variant" />
      </div>
    </div>

    <CatalogueRating v-if="!isAnonymized" :variant="variant" class="px-5 md:px-10 mb-9" />

    <div v-if="variant.commentsAllowed" class="px-5 md:px-10 mb-9">
      <ContentMessages v-if="!isAnonymized" :content="variant" :type="CHAT_MESSAGE_TYPE.CATALOGUE" />
    </div>
  </div>
</template>

<script setup>
import _ from 'lodash-es';
import { CHAT_MESSAGE_TYPE } from "@utils/constants";
import { siteSettings } from "@settings/site.settings";
import CatalogueFavorite from "@components/catalogue/catalogue-details/catalogue-favorite";
import CatalogueLikes from "@components/catalogue/catalogue-details/catalogue-likes";
import CatalogueRating from "@components/catalogue/catalogue-details/catalogue-rating";
import CatalogueViews from "@components/catalogue/catalogue-details/catalogue-views";
import StockAddButton from "@components/stock/stock-add-button";
import CatalogueTagParameters from "@components/catalogue/catalogue-tag-parameters";
import ContentDetailGallery from "@components/content/content-details/content-detail-gallery";
import ContentMessages from "@components/content/content-details/content-messages";
import StarIcon from "@components/icons/outline/star-icon";
import StarSolid from "@components/icons/solid/star-solid";

const props = defineProps({
  variant: {
    type: Object,
    required: true
  }
})

const { $eventBus } = useNuxtApp();
const config = useRuntimeConfig();

const imageSize = siteSettings.catalogueImage[config.public.layoutDesign]?.detail || siteSettings.catalogueImage.aquarist.detail;

const displayName = computed(() => catalogueDisplayName(props.variant));
const perex = computed(() => localized(props.variant, 'perex'));
const content = computed(() => localized(props.variant, 'content') || localized(props.variant, 'description'));
const ratingCount = computed(() => props.variant?.ratingCount || props.variant?.ratings?.length || 0);
const averageScore = computed(() => {
  if (!ratingCount.value) {
    return '0.0';
  }
  if (props.variant?.ratingScore !== undefined && props.variant?.ratingScore !== null) {
    return Number(props.variant.ratingScore).toFixed(1);
  }
  const ratings = props.variant?.ratings || [];
  return ratings.length
    ? (ratings.reduce((total, rating) => total + rating.score, 0) / ratings.length).toFixed(1)
    : '0.0';
});

const isAnonymized = computed(() => _.includes([STATUS.HIDDEN, STATUS.CLOSED], props.variant?.status));

const closeModal = () => {
    $eventBus.emit('modal:close');
}

const navigate = async () => {
    closeModal();
    await navigatorTo(cataloguePostPath(props.variant));
}

</script>
